import { type FC } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RichTreeViewPro } from '@mui/x-tree-view-pro';

import { DECOMPOSITION_TREE_TEST_ID, type DecompositionTreeTestIdScope } from '../testIds';
import type { ItemCallbacks, ItemMeta, RichTreeItem } from '../types';
import type { ExtraItemProps } from '../types';
import { CustomTreeItem } from './CustomTreeItem';

type ItemSlotProps = NonNullable<React.ComponentProps<typeof RichTreeViewPro>['slotProps']>['item'];

const getItemId = (item: RichTreeItem) => item.id;
const getItemLabel = (item: RichTreeItem) => item.label;

type DecompositionTreeProps = {
  items: RichTreeItem[];
  itemMetaMap: Record<string, ItemMeta>;
  expandedItems: string[];
  onExpandedItemsChange: (event: React.SyntheticEvent | null, items: string[]) => void;

  nodeIcon?: string;
  nodeName?: string;
  loadingParentIds?: Set<string>;

  callbacks?: ItemCallbacks;
  onShowMore?: (parentId: string) => void;

  isMoveMode?: boolean;
  selectedMoveNodeId?: string | null;
  selectedParentId?: string | null | undefined;
  onSelectParent?: (treeId: string) => void;
  disabledParentTreeId?: string | null;
  testIdScope?: DecompositionTreeTestIdScope;
};

export const DecompositionTree: FC<DecompositionTreeProps> = ({
  items,
  itemMetaMap,
  callbacks,
  onShowMore,
  expandedItems,
  onExpandedItemsChange,
  nodeIcon,
  nodeName,
  loadingParentIds,
  isMoveMode = false,
  selectedMoveNodeId = null,
  selectedParentId,
  onSelectParent,
  disabledParentTreeId = null,
  testIdScope
}) => (
  <RichTreeViewPro
    data-testid={DECOMPOSITION_TREE_TEST_ID.tree(testIdScope)}
    items={items}
    getItemId={getItemId}
    getItemLabel={getItemLabel}
    expandedItems={expandedItems}
    onExpandedItemsChange={onExpandedItemsChange}
    expansionTrigger='iconContainer'
    slots={{
      collapseIcon: ExpandMoreIcon,
      expandIcon: ChevronRightIcon,
      item: CustomTreeItem
    }}
    disableSelection={isMoveMode}
    slotProps={{
      item: {
        itemMetaMap,
        callbacks,
        onShowMore,
        nodeIcon,
        nodeName,
        isMoveMode,
        selectedMoveNodeId,
        selectedParentId,
        onSelectParent,
        disabledParentTreeId,
        loadingParentIds,
        testIdScope
      } satisfies ExtraItemProps as ItemSlotProps
    }}
  />
);
